import {
  $addCustomKeyBody,
  AddCustomKeyBody,
  CustomKeyAttributes,
  CustomKeyMode,
  CustomKeyTypesEnum,
} from '@loyalty/shared-common/profile-api-client';
import { Button, MenuItem, Switch, TextField, Tooltip } from '@mui/material';
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { DesktopDateTimePicker } from '@mui/x-date-pickers';
import { Feature } from '@loyalty/shared-common';
import { QlfFormRow } from '@qualifio/ui-commons';
import type { Schema } from 'joi';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailForm } from '../../../shared/layout/DetailForm';
import { SuperAdminMarker } from '../../../shared/markers/SuperAdminMarker';
import { Title } from '../../../shared/layout/Title';
import { useFeatures } from '../../../features/FeaturesContext';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';

const customKeyTypeOptions = {
  string: {
    key: 'string',
    value: CustomKeyTypesEnum.STRING,
    text: 'String',
  },
  number: {
    key: 'number',
    value: CustomKeyTypesEnum.NUMBER,
    text: 'Number',
  },
  boolean: {
    key: 'boolean',
    value: CustomKeyTypesEnum.BOOLEAN,
    text: 'Boolean',
  },
  date: {
    key: 'date',
    value: CustomKeyTypesEnum.DATE,
    text: 'Date',
  },
};

export interface CustomKeyFormProps {
  customKey: CustomKeyAttributes;
  onSubmit: (customKey: AddCustomKeyBody) => Promise<void>;
}

// make sure the value is a valid date (otherwise return null)
function fallbackDateTimeToPickerValue(value: unknown): Date | null {
  if (value === undefined || value === null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function CustomKeyForm({ customKey, onSubmit }: CustomKeyFormProps) {
  const { t } = useTranslation();
  const { features } = useFeatures();
  const { write: customKeyWrite } = features[Feature.CUSTOM_KEY];

  const resolver = useJoiResolver($addCustomKeyBody as Schema<AddCustomKeyBody>);
  const form = useForm<AddCustomKeyBody>({
    mode: 'onChange',
    resolver,
    defaultValues: customKey,
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid },
  } = form;

  useEffect(() => {
    reset(customKey);
  }, [customKey, reset]);

  const forwardSubmit = useCallback(
    (value: AddCustomKeyBody) =>
      onSubmit({
        ...customKey,
        name: value.name,
        technicalName: value.technicalName,
        type: value.type,
        fallbackValue: value.fallbackValue,
        mode: value.mode,
      }),
    [customKey, onSubmit],
  );

  return (
    <DetailForm>
      <FormProvider {...form}>
        <DetailForm.Form>
          <CustomKeysForm />
        </DetailForm.Form>
      </FormProvider>
      <DetailForm.Footer>
        <Tooltip
          title={customKeyWrite ? '' : t('shared.action.permission.required')}
          placement="left"
        >
          <div>
            <Button
              color="green"
              onClick={handleSubmit(forwardSubmit, console.error)}
              disabled={
                !isValid || !(isDirty || customKey['isNew']) || isSubmitting || !customKeyWrite
              }
            >
              {t('generic.actions.save')}
            </Button>
          </div>
        </Tooltip>
      </DetailForm.Footer>
    </DetailForm>
  );
}

function CustomKeysForm() {
  const { control, setValue } = useFormContext<AddCustomKeyBody>();
  const { t } = useTranslation();
  const type = useWatch({ control, name: 'type' });
  const fallbackValue = useWatch({ control, name: 'fallbackValue' });
  useEffect(() => {
    setValue('fallbackValue', '', { shouldDirty: true, shouldValidate: true });
    setValue('mode', CustomKeyMode.IMPORT, { shouldDirty: true, shouldValidate: true });
  }, [setValue, type]);

  return (
    <>
      <Title>
        {t('custom-keys.form.title')}
        <SuperAdminMarker />
      </Title>
      <Controller
        control={control}
        name="name"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <QlfFormRow label={t('custom-keys.key.name')}>
            <TextField
              {...field}
              inputRef={ref}
              placeholder={t('custom-keys.inputs.name')}
              error={!!error}
              helperText={error?.message}
              required
            />
          </QlfFormRow>
        )}
      />

      <Controller
        control={control}
        name="technicalName"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <QlfFormRow label={t('custom-keys.inputs.technicalName')}>
            <TextField
              {...field}
              inputRef={ref}
              placeholder={t('custom-keys.inputs.technicalName')}
              error={!!error}
              helperText={error?.message}
              required
            />
          </QlfFormRow>
        )}
      />

      <Controller
        control={control}
        name="type"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <QlfFormRow label={t('custom-keys.inputs.technicalType')}>
            <TextField
              {...field}
              inputRef={ref}
              error={!!error}
              helperText={error?.message}
              select
              required
            >
              {Object.entries(customKeyTypeOptions).map(([, value]) => (
                <MenuItem key={value.key} value={value.value}>
                  {value.text}
                </MenuItem>
              ))}
            </TextField>
          </QlfFormRow>
        )}
      />

      <Controller
        control={control}
        name="fallbackValue"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <QlfFormRow label={t('custom-keys.inputs.defaultValue')}>
            {type === CustomKeyTypesEnum.BOOLEAN ? (
              <TextField
                {...field}
                inputRef={ref}
                error={!!error}
                helperText={error?.message}
                select
              >
                <MenuItem value="">{t('custom-keys.inputs.defaultValueNone')}</MenuItem>
                <MenuItem value="true">{t('custom-keys.inputs.defaultValueTrue')}</MenuItem>
                <MenuItem value="false">{t('custom-keys.inputs.defaultValueFalse')}</MenuItem>
              </TextField>
            ) : type === CustomKeyTypesEnum.NUMBER ? (
              <TextField
                {...field}
                inputRef={ref}
                type="number"
                placeholder={t('custom-keys.inputs.defaultValuePlaceholderNumber')}
                error={!!error}
                helperText={error?.message}
                inputProps={{ step: 'any' }}
              />
            ) : type === CustomKeyTypesEnum.DATE ? (
              <DesktopDateTimePicker
                inputFormat="dd/MM/yyyy HH:mm"
                inputRef={ref}
                {...field}
                value={fallbackDateTimeToPickerValue(field.value)}
                onChange={date => field.onChange(date?.toJSON() ?? undefined)}
                renderInput={params => (
                  <TextField {...params} error={!!error} helperText={error?.message} />
                )}
              />
            ) : (
              <TextField
                {...field}
                inputRef={ref}
                placeholder={t('custom-keys.inputs.defaultValuePlaceholderString')}
                error={!!error}
                helperText={error?.message}
              />
            )}
          </QlfFormRow>
        )}
      />

      {!!fallbackValue && (
        <Controller
          control={control}
          name="mode"
          render={({ field }) => (
            <QlfFormRow label={t('custom-keys.inputs.force')}>
              <Switch
                checked={field.value === CustomKeyMode.FORCE}
                onChange={(_, checked) =>
                  field.onChange(checked ? CustomKeyMode.FORCE : CustomKeyMode.IMPORT)
                }
              />
            </QlfFormRow>
          )}
        />
      )}
    </>
  );
}
